// src/components/AdminPortal/UserManagement/Roles.js
import { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import Axios from "../../../utils/axios";
import "../UserManagement.css";
import ContentLoader from "../../../global/Content/ContentLoader";

const Roles = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    applicationId: "",
    applicationRoleName: "",
    description: "",
  });

  const fetchApplications = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/application/getAllApplications`
      );
      setApplications(response?.data || []);

      if (response?.data?.length > 0) {
        setSelectedApplicationId(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load applications");
    }
  };

  // ---------------------------------------
  // FETCH ROLES FOR SELECTED APPLICATION
  // ---------------------------------------
  const fetchRoles = async () => {
    if (!selectedApplicationId) return;
    setIsRefreshing(true);

    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/applicationRole/getAllApplicationRolesByApplicationId`,
        { params: { applicationId: selectedApplicationId } }
      );

      setRoles(response?.data || []);
    } catch (error) {
      toast.error("Failed to load roles");
    } finally {
      setIsRefreshing(false);
    }
  };

  // ---------------------------------------
  // FETCH ROLE BY ID  (VERSION A)
  // ---------------------------------------
  const fetchRoleById = async (id) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/applicationRole/getApplicationRoleById`,
        { params: { id } }
      );
      return response?.data;
    } catch (error) {
      toast.error("Failed to load role");
    }
  };

  // ---------------------------------------
  // CREATE ROLE
  // ---------------------------------------
  const createRole = async (data) => {
    try {
      const response = await Axios.post(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/applicationRole/createApplicationRole`,
        data
      );
      return response.data;
    } catch (error) {
      toast.error("Failed to create role");
    }
  };

  // ---------------------------------------
  // UPDATE ROLE
  // ---------------------------------------
  const updateRole = async (id, data) => {
    try {
      const response = await Axios.put(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/applicationRole/updateApplicationRole`,
        data,
        { params: { id } }
      );
      return response.data;
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  // ---------------------------------------
  // DELETE ROLE
  // ---------------------------------------
  const deleteRole = async (id) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_AUTH_BASE_URL}/api/applicationRole/deleteApplicationRole`,
        { params: { id } }
      );
      return true;
    } catch (error) {
      toast.error("Failed to delete role");
      return false;
    }
  };

  // Load applications
  useEffect(() => {
    fetchApplications();
  }, []);

  // Load roles when application changes
  useEffect(() => {
    fetchRoles();
  }, [selectedApplicationId]);

  const filteredRoles = roles.filter((role) =>
    role.applicationRoleName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.applicationId)
      newErrors.applicationId = "Application required";
    if (!formData.applicationRoleName)
      newErrors.applicationRoleName = "Role name required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNew = () => {
    setCurrentRole(null);
    setFormData({
      applicationId: selectedApplicationId,
      applicationRoleName: "",
      description: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = async (role) => {
    const latest = await fetchRoleById(role.id);
    if (!latest) return;

    setCurrentRole(latest);
    setFormData({
      applicationId: latest.applicationId,
      applicationRoleName: latest.applicationRoleName,
      description: latest.description,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    setIsLoading(true);

    const success = await deleteRole(roleToDelete.id);

    if (success) {
      setRoles(roles.filter((r) => r.id !== roleToDelete.id));
      toast.success("Role deleted");
    }

    setIsLoading(false);
    setDeleteModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      applicationId: formData.applicationId,
      applicationRoleName: formData.applicationRoleName,
      description: formData.description,
    };

    setIsLoading(true);

    if (currentRole) {
      const updated = await updateRole(currentRole.id, payload);
      setRoles(roles.map((r) => (r.id === currentRole.id ? updated : r)));
      toast.success("Updated");
    } else {
      const created = await createRole(payload);
      setRoles([...roles, created]);
      toast.success("Created");
    }

    setIsLoading(false);
    setIsModalOpen(false);
  };

  return (
    <div className="table-container">
      <h2>Application Roles</h2>
      <br />

      {/* Application selector */}
      <div className="app-dropdown-container">
        <label htmlFor="application-select" className="app-dropdown-label">
          Application
        </label>
        <select
          id="application-select"
          className="app-dropdown"
          value={selectedApplicationId}
          onChange={(e) => setSelectedApplicationId(e.target.value)}
        >
          <option value="">-- Select Application --</option>
          {applications.map((app) => (
            <option key={app.id} value={app.id}>
              {app.applicationName}
            </option>
          ))}
        </select>
      </div>

      {/* Search and New */}
      <div className="search-and-new">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button onClick={() => setSearchTerm("")} className="clear-button">
          Clear
        </button>
        <button onClick={handleNew} className="new-button" disabled={isLoading}>
          <FiPlus /> New
        </button>
      </div>

      {isRefreshing && !roles.length ? (
        <div className="loading">
          <ContentLoader />
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Role Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoles.map((role) => (
              <tr key={role.id}>
                <td>{role?.applicationRoleName}</td>
                <td>{role?.description}</td>
                <td className="action-buttons">
                  <button onClick={() => handleEdit(role)}>
                    <FiEdit />
                  </button>
                  <button onClick={() => handleDelete(role)}>
                    <FiTrash2 className="delete-icon" />
                  </button>
                </td>
              </tr>
            ))}
            {filteredRoles.length === 0 && (
              <tr>
                <td colSpan="3">No roles found</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{currentRole ? "Edit Role" : "New Role"}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name*</label>
                <input
                  name="applicationRoleName"
                  value={formData.applicationRoleName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      applicationRoleName: e.target.value,
                    })
                  }
                  disabled={isLoading}
                />
                {errors.applicationRoleName && (
                  <p className="text-danger">{errors.applicationRoleName}</p>
                )}
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔹 Delete Modal – same style as Add/Edit */}
      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Delete Role</h2>
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
            >
              <div className="form-group">
                <label>Confirmation</label>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{roleToDelete?.applicationRoleName}</strong>?
                </p>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setDeleteModalOpen(false)}
                  className="cancel-button"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  style={{ backgroundColor: " #28a745" }}
                  disabled={isLoading}
                >
                  {isLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
